// Цвета клиентов на экранах: действующий цвет (свой → группы → по умолчанию), оттенки под светлую и тёмную тему,
// окно выбора цвета (готовые, палитра, точный цвет).
import { CLIENT_COLORS, colorLabel, effColorKey, hexToHsl, hslToHex, isHexColor, type ClientOut, type SessionOut }
  from '@praktika/core';
import { $, $$, esc } from './dom.ts';
import { clientById, groupOf } from './data.ts';
import { closeSheet, openSheet, opt } from './sheet.ts';
import { S } from './state.ts';

export function colorOf(c: { color: string; groupId: string } | null): string {
  return c ? effColorKey(c, groupOf(c), S.settings) : '';
}

export interface ColorCss { bg: string; fg: string; dot: string }

// Именованный цвет — CSS-переменные темы; произвольный hex — оттенки считаем сами под светлую и тёмную тему.
export function colorCss(k: string): ColorCss {
  if (!k) return { bg: 'var(--t-blue)', fg: 'var(--on-blue)', dot: 'var(--oc-blue)' };
  if (!isHexColor(k)) return { bg: 'var(--c-' + k + ')', fg: 'var(--oc-' + k + ')', dot: 'var(--oc-' + k + ')' };
  const dark = document.documentElement.getAttribute('data-theme') === 'dark', h = hexToHsl(k);
  return dark ? { bg: hslToHex(h.h, Math.min(h.s, 45), 24), fg: hslToHex(h.h, Math.min(h.s, 70), 80),
    dot: hslToHex(h.h, Math.max(h.s, 30), 66) }
    : { bg: hslToHex(h.h, Math.min(h.s, 80), 88), fg: hslToHex(h.h, Math.min(h.s, 75), 30),
      dot: hslToHex(h.h, Math.max(h.s, 30), 42) };
}

export function colName(k: string): string { return colorLabel(k) || (isHexColor(k) ? 'свой цвет' : ''); }

export function cdot(c: ClientOut | null): string {
  const k = colorOf(c);
  return k ? '<i class="cdot" style="background:' + colorCss(k).dot + '"></i>' : '';
}

function sessionColor(s: SessionOut): string {
  const c = clientById(s.clientId);
  return c ? colorOf(c) : effColorKey(null, groupOf(s), S.settings);
}

// точка сессии в неделе/месяце; резерв — кружок
export function dotAttr(s: SessionOut): string {
  const k = sessionColor(s);
  if (s.status === 'reserve') return ' class="r" style="border-color:' + colorCss(k).dot + '"';
  return ' style="background:' + colorCss(k).dot + '"';
}

// Резерв показывается штриховкой в цвете клиента (или общем цвете): переменные для CSS.
export function resVars(c: ClientOut | null, s: SessionOut): string {
  const k = c ? colorOf(c) : effColorKey(null, groupOf(s), S.settings), cc = colorCss(k);
  return '--rb:' + cc.bg + ';--rd:' + cc.dot;
}

// кружок с цветом и подпись для строк настроек
export function colv(k: string): string {
  return k ? '<i class="cdot big" style="background:' + (isHexColor(k) ? k : colorCss(k).dot) +
    ';border:1px solid var(--line3)"></i>' + esc(colName(k)) : '';
}

export const BG_LIGHT: Array<[string, string]> = [['', 'Как обычно'], ['#ffffff', 'Белый'], ['#f7f3ec', 'Тёплый'],
  ['#eef5f0', 'Мятный'], ['#f2eff9', 'Лавандовый'], ['#eaf1f9', 'Голубой']];
export const BG_DARK: Array<[string, string]> = [['', 'Как обычно'], ['#000000', 'Чёрный'], ['#16181d', 'Графит'],
  ['#0b1220', 'Тёмно-синий'], ['#121a16', 'Тёмно-зелёный'], ['#1a1420', 'Тёмно-лиловый']];

export function bgName(list: Array<[string, string]>, v: string): string {
  const hit = list.find((x) => x[0] === (v || ''));
  return hit ? hit[1] : (isHexColor(v) ? 'свой цвет' : v);
}
export function bgv(list: Array<[string, string]>, v: string, dark: boolean): string {
  return '<i class="cdot big" style="background:' + esc(v || (dark ? '#0f131a' : '#f3f5f8')) +
    ';border:1px solid var(--line3)"></i>' + esc(bgName(list, v));
}

export interface ColorSheetOpts { title?: string; sub?: string; current?: string; kind?: 'client' | 'bglight' | 'bgdark';
  none?: string; noneHint?: string }

// Единое окно выбора цвета: готовые цвета, палитра оттенков, точный цвет, сброс.
export function colorSheet(o: ColorSheetOpts, cb: (v: string) => void): void {
  const cur = String(o.current || '').toLowerCase(), kind = o.kind || 'client', dark = kind === 'bgdark',
    client = kind === 'client';
  const presets = client ? CLIENT_COLORS.map((k) => ({ v: k[0], bg: 'var(--c-' + k[0] + ')', fg: 'var(--oc-' + k[0] + ')',
    label: k[1] })) : (dark ? BG_DARK : BG_LIGHT).filter((x) => x[0]).map((x) => ({ v: x[0], bg: x[0],
    fg: dark ? '#e6e9ef' : '#1f2937', label: x[1] }));
  const hues = [0, 25, 50, 90, 150, 200, 240, 280, 320], Ls = client ? [40, 52, 64] : dark ? [8, 12, 17] : [97, 94, 90],
    sat = client ? 65 : dark ? 30 : 45;
  const pal: string[] = [];
  for (const l of Ls) {
    for (const h of hues) pal.push(hslToHex(h, sat, l));
    if (!client) pal.push(hslToHex(0, 0, l));
  }
  const cols = hues.length + (client ? 0 : 1), def = client ? '#2563eb' : dark ? '#0f131a' : '#f3f5f8',
    exact = isHexColor(cur) ? cur : def;
  const html = '<div class="ttl">' + esc(o.title || 'Цвет') + '</div>' + (o.sub ? '<p class="sub">' + esc(o.sub) + '</p>' : '') +
    '<div class="swl">Готовые</div><div class="swatches">' + presets.map((q) => '<button class="sw' + (q.v === cur ? ' on' : '') +
    '" data-c="' + esc(q.v) + '" style="background:' + q.bg + ';color:' + q.fg + '" title="' + esc(q.label) + '">' +
    (q.v === cur ? '✓' : '') + '</button>').join('') + '</div>' +
    '<div class="swl">Палитра</div><div class="pal" style="grid-template-columns:repeat(' + cols + ',1fr)">' +
    pal.map((h) => '<button class="pc' + (h === cur ? ' on' : '') + '" data-c="' + h + '" style="background:' + h +
    '"></button>').join('') + '</div>' +
    '<label class="kv"><span class="k">Точный цвет</span><span class="v chev"><i class="cdot big" style="background:' +
    exact + ';border:1px solid var(--line3)"></i>' + (isHexColor(cur) ? esc(cur.toUpperCase()) : 'выбрать…') +
    '</span><input type="color" data-exact value="' + exact +
    '" style="position:absolute;opacity:0;width:1px;height:1px;left:0;top:0"></label>' +
    (o.none ? opt(!cur ? 'ok' : '', '', o.none, o.noneHint || '') : '');
  const sh = openSheet(html);
  $$('[data-c]', sh).forEach((b) => { b.onclick = () => { closeSheet(); cb(b.getAttribute('data-c')!); }; });
  const ex = $<HTMLInputElement>('[data-exact]', sh);
  if (ex) ex.onchange = () => { const v = String(ex.value || '').toLowerCase(); if (isHexColor(v)) { closeSheet(); cb(v); } };
  const no = $('button.opt', sh);
  if (no) no.onclick = () => { closeSheet(); cb(''); };
}
